import { useCanvasApi } from "@/pages/canvas/context/CanvasContext";
import {
  CANVAS_EXPAND_FILE_RECORD_STATE_KEY,
  readExpandFileRecordId,
} from "@/pages/canvas/utils/canvasBlockNavigation";
import {
  isBlockNodeData,
  type BlockDisplayRecord,
} from "@cohesive/ui-components";
import type { Node } from "@xyflow/react";
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export function fileRecordIdOf(node: Node): string | undefined {
  return (node.data as { entity?: { data?: { id?: unknown } } } | undefined)
    ?.entity?.data?.id as string | undefined;
}

/**
 * Honors an `expandFileRecordId` navigation request (see
 * canvasBlockNavigation): once the block's node is in the document, center
 * on it and open it in the lightbox. The request is cleared from history
 * state first, so closing the lightbox — or a later re-render — can never
 * reopen it. A request for a record that never appears (not on this canvas,
 * or not a block) is simply never honored. Whether the node is a block is
 * read as the node router reads it: the record's display first.
 */
export function useExpandFileRecordOnArrival(
  nodes: Node[],
  fileRecordById?: (id: string) => BlockDisplayRecord | null | undefined,
) {
  const location = useLocation();
  const navigate = useNavigate();
  const canvas = useCanvasApi();
  const requested = readExpandFileRecordId(location.state);

  useEffect(() => {
    if (!requested) return;
    const node = nodes.find(
      (n) =>
        n.type === "entity" &&
        fileRecordIdOf(n) === requested &&
        isBlockNodeData(n.data, fileRecordById?.(requested)),
    );
    if (!node) return;
    const { [CANVAS_EXPAND_FILE_RECORD_STATE_KEY]: _handled, ...rest } =
      (location.state ?? {}) as Record<string, unknown>;
    navigate(location.pathname + location.search, {
      replace: true,
      state: rest,
    });
    // Bring the node into view underneath, so dismissing the overlay lands
    // the user on the block rather than wherever the viewport was restored.
    window.dispatchEvent(
      new CustomEvent("canvas:focus-node", {
        detail: { nodeId: node.id, keepZoom: true },
      }),
    );
    canvas.expandNode(node.id);
  }, [
    requested,
    nodes,
    fileRecordById,
    canvas,
    navigate,
    location.pathname,
    location.search,
    location.state,
  ]);
}
